// File: frontend/src/LanguageContext.js

import React, { createContext, useContext, useState } from 'react';

const LanguageContext = createContext();

export const translations = {
  en: {
    // Navigation Tabs
    nav_dashboard: 'Dashboard',
    nav_calendar: 'Calendar',
    nav_attendance: 'Attendance',
    nav_leave: 'Leave Requests',
    nav_classes: 'Classes',
    nav_students: 'Students',
    nav_reports: 'Reports',
    nav_expenses: 'Expenses',
    nav_payroll: 'Payroll',
    nav_ledger: 'Fee Ledger',
    nav_rooms: 'Rooms',
    nav_portal: 'Student Portal',
    nav_materials: 'Materials',
    nav_payments: 'Payments',
    nav_profile: 'Profile',

    // Admin Controls
    admin_controls: 'Admin Controls',
    admin_desc: 'Manage staff, classes, students, bank accounts, and LINE services',
    admin_collapse: 'Collapse ▲',
    admin_expand: 'Expand ▼',
    admin_users: 'Staff & Users',
    admin_classes: 'Class Management',
    admin_students: 'Student Management',
    admin_banks: 'Bank Accounts',
    admin_line: 'LINE Services',
    admin_official_bot: 'Official Bot',
    admin_line_pay: 'LINE Pay',

    // Attendance & Cram Session
    att_daily_view: 'Daily View',
    att_weekly_view: 'Weekly View',
    att_single_view: 'Single Class',
    att_class_held: 'Class Held',
    att_class_not_held: 'Not Held / Cancelled',
    att_mark_held: '✓ Record Class Held',
    att_mark_not_held: '✗ Not Held',
    att_reset: '↺ Reset',
    att_status_held: 'Class Held',
    att_status_not_held: 'Not Held',
    att_status_pending: 'Pending Roll Call',
    att_present: 'Present',
    att_absent: 'Absent',
    att_late: 'Late',
    att_excused: 'Excused',
    att_fixed_contract_rate: 'Flat Contract Rate',
    att_cram_notice: 'Whole-class contract rate, billed hourly. Individual roll call not required to secure pay.',
    att_view_roster: '👥 View Student Roster ▼',
    att_hide_roster: '👥 Hide Student Roster ▲',
    att_save_attendance: 'Save Attendance',
    att_saving: 'Saving...',
    att_completed: 'Completed',
    att_mark_all_present: 'Mark All Present',
    att_schedule_makeup: '📅 Schedule Make-Up',
    att_schedule_makeup_prompt_title: 'Schedule Make-Up?',
    att_schedule_makeup_modal_title: '📅 Schedule Single Make-Up Class',
    att_makeup_date: 'Make-Up Date',
    att_makeup_notice: 'This is a one-off scheduled class that will not repeat weekly.',
    att_start_time: 'Start Time',
    att_end_time: 'End Time',
    att_class_format: 'Class Format',
    att_format_in_person: 'In-Person',
    att_format_online: 'Online',
    att_assign_teacher: 'Assigned Teacher',
    att_use_class_teacher: '-- Use Class Teacher --',
    att_makeup_notes: 'Make-Up Notes',
    att_makeup_notes_placeholder: 'e.g. Make-up for sick leave on 9/17',
    att_confirm_schedule: 'Confirm & Schedule Class',
    att_skip_makeup: 'Skip For Now',
    att_yes_schedule: '📅 Yes, Schedule Make-Up',
    att_absent_no_fee: 'Absent (No Fee)',
    att_confirm_transfer: 'Confirm Transfer',
    att_confirm_cash: 'Confirm Cash',

    // Students & Multi-Pick
    stu_management: 'Student Management',
    stu_selected: 'selected',
    stu_export_csv: 'Export CSV',
    stu_delete_selected: 'Delete',
    stu_assign_classes: 'Assign to Class(es)',
    stu_all: 'All Students',
    stu_private: 'Private Tutoring',
    stu_cram: 'Cram Schools',
    stu_select_all: 'Select All',
    stu_clear: 'Clear',
    stu_multi_pick_title: 'Multi-Pick Student Enrollment',
    stu_multi_pick_desc: 'Check multiple students to enroll them together in one click',
    stu_enroll_selected: 'Enroll Selected',
    stu_confirm_enroll: 'Confirm & Enroll Now ➔',
    stu_remove_selected: 'Remove',
    stu_enrolled_roster: 'Enrolled Roster',
    stu_available_students: 'Available Students',
    stu_no_enrolled: 'No students currently enrolled.',
    stu_all_enrolled: 'All available students are already enrolled!',
    stu_picked: 'Picked',
    stu_pick: '+ Pick',
    stu_category: 'Student Category',
    stu_private_desc: 'Private Tutoring / 1-on-1',
    stu_cram_desc: 'Cram School / Group Branch',
    stu_branch_name: 'Cram School / Branch Name',
    stu_full_name: 'Student Full Name',
    stu_birthday: 'Birthday (Optional)',
    stu_trial_student: 'Trial / Observation Student',
    stu_notes: 'Notes (Optional)',
    stu_add_student: '+ Add Student',
    stu_search_placeholder: 'Search students by name, email, cram school...',
    cls_multi_pick: 'Multi-Pick Student Enrollment',

    // Dashboard
    dash_monthly_projection: 'Monthly Revenue Projection',
    dash_current_earned: 'Current Month Income',
    dash_potential_gap: 'Potential Balance / Variance',
    dash_tutoring_retention: 'Tutoring Student Retention',

    // Payment Methods
    pay_cash: '💵 Cash',
    pay_bank: '🏦 Bank Transfer',
    pay_line_pay: '🟢 LINE Pay',
    pay_per_class: 'Per-Class',
    pay_monthly: 'Monthly Bill',

    // Common
    common_save: 'Save',
    common_save_changes: 'Save Changes',
    common_cancel: 'Cancel',
    common_edit: 'Edit',
    common_delete: 'Delete',
    common_search: 'Search',
    common_logout: 'Logout',
    common_welcome: 'Welcome',
    common_back: 'Back',
    common_actions: 'Actions',
    common_status: 'Status',
    common_notes: 'Notes',
    common_date: 'Date',
    common_time: 'Time',
    common_teacher: 'Teacher',
  },
  zh: {
    // Navigation Tabs
    nav_dashboard: '儀表板',
    nav_calendar: '行事曆',
    nav_attendance: '點名出席',
    nav_leave: '請假管理',
    nav_classes: '班級管理',
    nav_students: '學生名單',
    nav_reports: '統計報表',
    nav_expenses: '費用支出',
    nav_payroll: '薪資計算',
    nav_ledger: '收費帳本',
    nav_rooms: '教室場地',
    nav_portal: '學生專區',
    nav_materials: '教材下載',
    nav_payments: '繳費紀錄',
    nav_profile: '個人資料',

    // Admin Controls
    admin_controls: '系統管理',
    admin_desc: '管理師資、班級、學生、銀行帳戶及 LINE 整合',
    admin_collapse: '收起 ▲',
    admin_expand: '展開 ▼',
    admin_users: '教職員管理',
    admin_classes: '班級管理',
    admin_students: '學生管理',
    admin_banks: '轉帳帳戶',
    admin_line: 'LINE 服務',
    admin_official_bot: '官方帳號推播',
    admin_line_pay: 'LINE Pay 收款',

    // Attendance & Cram Session
    att_daily_view: '每日檢視',
    att_weekly_view: '每週檢視',
    att_single_view: '個別班級',
    att_class_held: '已上課',
    att_class_not_held: '未上課 / 取消',
    att_mark_held: '✓ 記錄已上課',
    att_mark_not_held: '✗ 未上課',
    att_reset: '↺ 重設',
    att_status_held: '已上課',
    att_status_not_held: '未上課',
    att_status_pending: '待記錄上課',
    att_present: '出席',
    att_absent: '缺席',
    att_late: '遲到',
    att_excused: '請假',
    att_fixed_contract_rate: '整班固定合約費',
    att_cram_notice: '安親課堂為整班合約時薪，依課堂時數計算，不需逐一為學生點名即可計算課時薪資。',
    att_view_roster: '👥 展開檢視學生名單 ▼',
    att_hide_roster: '👥 收起學生名單 ▲',
    att_save_attendance: '儲存出席記錄',
    att_saving: '儲存中...',
    att_completed: '已完成點名',
    att_mark_all_present: '全體出席',
    att_schedule_makeup: '📅 安排補課',
    att_schedule_makeup_prompt_title: '安排補課？',
    att_schedule_makeup_modal_title: '📅 安排單次補課',
    att_makeup_date: '補課日期',
    att_makeup_notice: '此課堂為單次專屬補課，僅在該日期排程，不會每週重複。',
    att_start_time: '開始時間',
    att_end_time: '結束時間',
    att_class_format: '授課形式',
    att_format_in_person: '實體課',
    att_format_online: '線上課',
    att_assign_teacher: '指定授課教師',
    att_use_class_teacher: '-- 使用原班級教師 --',
    att_makeup_notes: '補課備註',
    att_makeup_notes_placeholder: '例如：補 9/17 生病請假',
    att_confirm_schedule: '確認安排補課',
    att_skip_makeup: '暫不安排',
    att_yes_schedule: '📅 是，安排補課',
    att_absent_no_fee: '缺席 (請假不計費)',
    att_confirm_transfer: '確認轉帳',
    att_confirm_cash: '確認現金收現',

    // Students & Multi-Pick
    stu_management: '學生管理',
    stu_selected: '位已選取',
    stu_export_csv: '匯出 CSV',
    stu_delete_selected: '刪除所選',
    stu_assign_classes: '分配至班級',
    stu_all: '所有學生',
    stu_private: '個別家教',
    stu_cram: '安親補習班',
    stu_select_all: '全選',
    stu_clear: '清除',
    stu_multi_pick_title: '批量加入學生',
    stu_multi_pick_desc: '勾選多位學生，一鍵批次加入此班級',
    stu_enroll_selected: '加入所選學生',
    stu_confirm_enroll: '確認並立即加入 ➔',
    stu_remove_selected: '移出所選',
    stu_enrolled_roster: '已在班名單',
    stu_available_students: '可加入學生',
    stu_no_enrolled: '目前無學生在班。',
    stu_all_enrolled: '所有學生皆已加入本班！',
    stu_picked: '已選',
    stu_pick: '+ 選擇',
    stu_category: '學生分類',
    stu_private_desc: '個別家教 / 專屬一對一',
    stu_cram_desc: '安親補習班 / 分校團班',
    stu_branch_name: '補習班 / 分校名稱',
    stu_full_name: '學生姓名',
    stu_birthday: '生日 (選填)',
    stu_trial_student: '試聽 / 觀課學生',
    stu_notes: '備註 (選填)',
    stu_add_student: '+ 新增學生',
    stu_search_placeholder: '搜尋學生姓名、信箱、補習班...',
    cls_multi_pick: '批量加入學生',

    // Dashboard
    dash_monthly_projection: '月度預估總收入',
    dash_current_earned: '本月至今實收',
    dash_potential_gap: '預估與實收差額',
    dash_tutoring_retention: '家教學生留存率',

    // Payment Methods
    pay_cash: '💵 現金收現',
    pay_bank: '🏦 銀行轉帳',
    pay_line_pay: '🟢 LINE Pay',
    pay_per_class: '單堂結算',
    pay_monthly: '月結帳單',

    // Common
    common_save: '儲存',
    common_save_changes: '儲存變更',
    common_cancel: '取消',
    common_edit: '編輯',
    common_delete: '刪除',
    common_search: '搜尋',
    common_logout: '登出',
    common_welcome: '歡迎',
    common_back: '返回',
    common_actions: '操作',
    common_status: '狀態',
    common_notes: '備註',
    common_date: '日期',
    common_time: '時間',
    common_teacher: '老師',
  }
};

export function LanguageProvider({ children }) {
  const [language, setLanguageState] = useState(() => {
    try {
      return localStorage.getItem('irie_lang') || 'en';
    } catch {
      return 'en';
    }
  });

  const setLanguage = (lang) => {
    const validLang = lang === 'zh' ? 'zh' : 'en';
    setLanguageState(validLang);
    try {
      localStorage.setItem('irie_lang', validLang);
    } catch {}
  };

  const toggleLanguage = () => {
    setLanguage(language === 'en' ? 'zh' : 'en');
  };

  const t = (key, fallback = '') => {
    const currentDict = translations[language] || translations.en;
    if (currentDict && currentDict[key] !== undefined) {
      return currentDict[key];
    }
    const fallbackDict = translations.en;
    if (fallbackDict && fallbackDict[key] !== undefined) {
      return fallbackDict[key];
    }
    return fallback || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    // Fallback if rendered outside provider
    return {
      language: 'en',
      setLanguage: () => {},
      toggleLanguage: () => {},
      t: (key, fallback = '') => fallback || key,
    };
  }
  return context;
}
